import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { getToken } from '../../../utils/token';
import { searchMarkets, type MarketSearchResult } from '../../../services/marketSearchService';
import { apiClient } from '../../../lib/apiClient';
import type { DivisionSearchResult, UserSearchResult } from '../InsightsPage.types';

interface UserSearchResponse {
  data?: UserSearchResult[];
}

interface DivisionSearchResponse {
  data?: DivisionSearchResult[];
}

type DivisionSearchPayload = DivisionSearchResult[] | DivisionSearchResponse;

interface UseInsightsMentionSearchesOptions {
  userSearchQuery: string | null;
  isUserSearchOpen: boolean;
  setUserSearchResults: Dispatch<SetStateAction<UserSearchResult[]>>;
  setIsUserSearchLoading: Dispatch<SetStateAction<boolean>>;
  setUserSearchActiveIndex: Dispatch<SetStateAction<number>>;
  divisionSearchQuery: string | null;
  isDivisionSearchOpen: boolean;
  setDivisionSearchResults: Dispatch<SetStateAction<DivisionSearchResult[]>>;
  setIsDivisionSearchLoading: Dispatch<SetStateAction<boolean>>;
  setDivisionSearchActiveIndex: Dispatch<SetStateAction<number>>;
  marketSearchQuery: string | null;
  isMarketSearchOpen: boolean;
  setMarketSearchResults: Dispatch<SetStateAction<MarketSearchResult[]>>;
  setIsMarketSearchLoading: Dispatch<SetStateAction<boolean>>;
  setMarketSearchActiveIndex: Dispatch<SetStateAction<number>>;
}

const MAX_MENTION_RESULTS = 8;

export const useInsightsMentionSearches = ({
  userSearchQuery,
  isUserSearchOpen,
  setUserSearchResults,
  setIsUserSearchLoading,
  setUserSearchActiveIndex,
  divisionSearchQuery,
  isDivisionSearchOpen,
  setDivisionSearchResults,
  setIsDivisionSearchLoading,
  setDivisionSearchActiveIndex,
  marketSearchQuery,
  isMarketSearchOpen,
  setMarketSearchResults,
  setIsMarketSearchLoading,
  setMarketSearchActiveIndex,
}: UseInsightsMentionSearchesOptions): void => {
  const [allMarkets, setAllMarkets] = useState<MarketSearchResult[]>([]);

  useEffect(() => {
    if (!userSearchQuery || !isUserSearchOpen) {
      setIsUserSearchLoading(false);
      return;
    }

    const abortController = new AbortController();
    const timer = window.setTimeout(() => {
      const runUserSearch = async (): Promise<void> => {
        setIsUserSearchLoading(true);

        try {
          const token = getToken();
          if (!token) {
            setUserSearchResults([]);
            return;
          }

          const payload = await apiClient()
            .get('api/users/search', {
              searchParams: { query: userSearchQuery },
              signal: abortController.signal,
            })
            .json<UserSearchResponse>();

          const results = Array.isArray(payload.data) ? payload.data.slice(0, MAX_MENTION_RESULTS) : [];
          setUserSearchResults(results);
          setUserSearchActiveIndex(0);
        } catch {
          if (!abortController.signal.aborted) {
            setUserSearchResults([]);
          }
        } finally {
          if (!abortController.signal.aborted) {
            setIsUserSearchLoading(false);
          }
        }
      };

      void runUserSearch();
    }, 250);

    return () => {
      abortController.abort();
      window.clearTimeout(timer);
    };
  }, [isUserSearchOpen, setIsUserSearchLoading, setUserSearchActiveIndex, setUserSearchResults, userSearchQuery]);

  useEffect(() => {
    if (!divisionSearchQuery || !isDivisionSearchOpen) {
      setIsDivisionSearchLoading(false);
      return;
    }

    const abortController = new AbortController();
    const timer = window.setTimeout(() => {
      const runDivisionSearch = async (): Promise<void> => {
        setIsDivisionSearchLoading(true);

        try {
          const token = getToken();
          if (!token) {
            setDivisionSearchResults([]);
            return;
          }

          const payload = await apiClient()
            .get('api/admin/v2/division/search', {
              searchParams: { q: divisionSearchQuery },
              signal: abortController.signal,
            })
            .json<DivisionSearchPayload>();

          const results = Array.isArray(payload) ? payload : Array.isArray(payload.data) ? payload.data : [];

          const uniqueResults = results.filter(
            (result, index, collection) => index === collection.findIndex((entry) => entry.uuid === result.uuid),
          );

          setDivisionSearchResults(uniqueResults.slice(0, MAX_MENTION_RESULTS));
          setDivisionSearchActiveIndex(0);
        } catch {
          if (!abortController.signal.aborted) {
            setDivisionSearchResults([]);
          }
        } finally {
          if (!abortController.signal.aborted) {
            setIsDivisionSearchLoading(false);
          }
        }
      };

      void runDivisionSearch();
    }, 250);

    return () => {
      abortController.abort();
      window.clearTimeout(timer);
    };
  }, [
    divisionSearchQuery,
    isDivisionSearchOpen,
    setDivisionSearchActiveIndex,
    setDivisionSearchResults,
    setIsDivisionSearchLoading,
  ]);

  useEffect(() => {
    const abortController = new AbortController();

    const loadMarkets = async (): Promise<void> => {
      try {
        const token = getToken();
        if (!token) {
          setAllMarkets([]);
          return;
        }

        const markets = await searchMarkets('', token, abortController.signal);
        setAllMarkets(markets);
      } catch {
        if (!abortController.signal.aborted) {
          setAllMarkets([]);
        }
      }
    };

    void loadMarkets();

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (!marketSearchQuery || !isMarketSearchOpen) {
      setMarketSearchResults([]);
      setIsMarketSearchLoading(false);
      return;
    }

    const abortController = new AbortController();
    const timer = window.setTimeout(() => {
      setIsMarketSearchLoading(true);

      try {
        const lowerQuery = marketSearchQuery.toLowerCase();
        const filteredMarkets = allMarkets.filter(
          (market) =>
            market.name.toLowerCase().includes(lowerQuery) ||
            market.marketUuid.toLowerCase().includes(lowerQuery) ||
            market.divisionUuid.toLowerCase().includes(lowerQuery) ||
            market.currency.toLowerCase().includes(lowerQuery),
        );

        if (!abortController.signal.aborted) {
          setMarketSearchResults(filteredMarkets.slice(0, MAX_MENTION_RESULTS));
          setMarketSearchActiveIndex(0);
        }
      } catch {
        if (!abortController.signal.aborted) {
          setMarketSearchResults([]);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsMarketSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
      abortController.abort();
    };
  }, [
    allMarkets,
    isMarketSearchOpen,
    marketSearchQuery,
    setIsMarketSearchLoading,
    setMarketSearchActiveIndex,
    setMarketSearchResults,
  ]);
};
